import { DestroyRef, Directive, ElementRef, effect, inject, input, output, untracked } from '@angular/core'
import { isKeyboardField } from '../../functions/is-keyboard-field'
import { HapticService } from '../../services/haptic-service/haptic.service'
import { OVERLAY_EASE_OUT, OVERLAY_RETURN_DURATION } from '../../animations/overlay-motion'

/** Vertical travel that dismisses the sheet on release, in pixels. */
const DISMISS_DISTANCE = 120
/** Downward speed that dismisses the sheet on release, in pixels per millisecond. */
const DISMISS_VELOCITY = 0.6
/** Shortest gap between move samples that yields a trustworthy velocity. */
const MIN_VELOCITY_SAMPLE_MS = 8
/** Travel needed to distinguish a content pull from a tap or sideways swipe. */
const CONTENT_DRAG_SLOP = 8
/** Spring-back motion shared by the panel and its backdrop. */
const RESTORE_TIMING = `${OVERLAY_RETURN_DURATION}ms ${OVERLAY_EASE_OUT}`
/** Text input types that can yield an unfocused vertical pull to the sheet. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password'])

/**
 * Shared touch controller for sheets. Keeping the state here makes every mobile sheet use
 * the same distance, velocity, and spring-back behavior.
 */
export class SheetDragController {
  private startY: number | undefined
  private offset = 0
  private velocity = 0
  private lastY = 0
  private lastTime = 0
  private height = 0

  /** Share of the panel height pulled down, from 0 at rest to 1 off screen. */
  get progress(): number {
    return this.height > 0 ? Math.min(1, this.offset / this.height) : 0
  }

  /** Whether the current drag has moved the panel down. A finished drag keeps its offset, so it does not count.
   * @returns Whether the panel moved in this drag.
   */
  get moved(): boolean {
    return this.startY !== undefined && this.offset > 0
  }

  /** Pixels the panel still has to travel to leave the screen. */
  get remaining(): number {
    return Math.max(0, this.height - this.offset)
  }

  /**
   * Records the touch origin and stops transitions so the panel tracks the finger.
   * @param {TouchEvent} event
   * @param {HTMLElement} panel
   */
  start(event: TouchEvent, panel: HTMLElement): void {
    const touch = event.touches[0]

    if (!touch) {
      return
    }

    this.startY = touch.clientY
    this.lastY = touch.clientY
    this.lastTime = event.timeStamp
    this.offset = 0
    this.velocity = 0
    this.height = panel.offsetHeight
    panel.style.transition = 'none'
    // Its own layer lets the panel move without repainting on every frame.
    panel.style.willChange = 'transform'
  }

  /**
   * Moves the panel with a downward drag.
   * @param {TouchEvent} event
   * @param {HTMLElement} panel
   */
  move(event: TouchEvent, panel: HTMLElement): void {
    const touch = event.touches[0]

    if (this.startY === undefined || !touch) {
      return
    }

    // Samples closer than one frame carry jitter, not speed; keep the previous velocity.
    const elapsed = event.timeStamp - this.lastTime

    if (elapsed >= MIN_VELOCITY_SAMPLE_MS) {
      this.velocity = (touch.clientY - this.lastY) / elapsed
      this.lastY = touch.clientY
      this.lastTime = event.timeStamp
    }

    this.offset = Math.max(0, touch.clientY - this.startY)

    if (this.offset > 0) {
      event.preventDefault()
    }

    panel.style.transform = `translateY(${this.offset}px)`
  }

  /**
   * Springs back, or marks the panel for dismissal when the threshold is reached.
   * @param {HTMLElement} panel Sheet panel.
   * @param {'distance' | 'distance-or-flick'} [dismissal=distance-or-flick] Content pulls use distance to avoid accidental dismissal.
   * @returns {boolean} Whether the sheet should close.
   */
  end(panel: HTMLElement, dismissal: 'distance' | 'distance-or-flick' = 'distance-or-flick'): boolean {
    if (this.startY === undefined) {
      return false
    }

    this.startY = undefined

    if (this.offset > DISMISS_DISTANCE || (dismissal === 'distance-or-flick' && this.velocity > DISMISS_VELOCITY)) {
      return true
    }

    this.restore(panel)

    return false
  }

  /**
   * Returns an interrupted gesture to its starting position.
   * @param {HTMLElement} panel
   */
  cancel(panel: HTMLElement): void {
    if (this.startY === undefined) {
      return
    }

    this.startY = undefined
    this.restore(panel)
  }

  /**
   * Restores the sheet without motion when the user prefers reduced motion.
   * @param {HTMLElement} panel
   */
  private restore(panel: HTMLElement): void {
    this.offset = 0
    panel.style.transition = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'none'
      : `transform ${RESTORE_TIMING}`
    panel.style.transform = ''
    panel.style.willChange = ''
  }
}

/** Adds swipe-down dismissal to a sheet header or content at its scroll boundary. */
@Directive({
  selector: '[awSheetDrag]',
  host: {
    '(touchstart)': 'onStart($event)',
    '(touchend)': 'onEnd()',
    '(touchcancel)': 'onCancel()'
  }
})
export class SheetDragDirective {
  /** Panel that follows the finger. */
  readonly awSheetDrag = input.required<HTMLElement>()
  /** Retained panels that follow the same pull as the active sheet page. */
  readonly sheetDragPanels = input<readonly HTMLElement[]>([])
  /** Backdrop that lightens as the sheet is pulled down. */
  readonly sheetDragBackdrop = input<HTMLElement>()
  /** Turns off swipe dismissal for surfaces shown as a full page. */
  readonly sheetDragDisabled = input(false)
  /** Takes a fresh downward pull that starts at the top; native scrolling keeps earlier gestures. */
  readonly sheetDragFromContent = input(false)
  /** Emits the pixels the panel still has to travel, so its exit can keep the same speed. */
  readonly dismissed = output<number>()
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly controller = new SheetDragController()
  private readonly haptics = inject(HapticService)
  private pendingTouch?: TouchEvent
  private dragging = false
  private panel?: HTMLElement
  private closedField?: HTMLElement

  constructor() {
    effect(() => this.syncPanel())

    const element = this.host.nativeElement
    // A host listener would check the sheet and every view above it on each move, including scrolls and page back
    // swipes that pass through this body. Moves only write styles, so they skip it.
    const move = (event: TouchEvent): void => this.onMove(event)

    element.addEventListener('touchmove', move, { passive: false })
    inject(DestroyRef).onDestroy(() => element.removeEventListener('touchmove', move))
  }

  /**
   * Starts a header drag or waits for a content pull. @param event Touch origin.
   * @param {TouchEvent} event
   */
  onStart(event: TouchEvent): void {
    const target = event.target

    this.onCancel()

    if (!this.panel || this.sheetDragDisabled() || event.touches.length !== 1) {
      return
    }

    if (this.sheetDragFromContent()) {
      if (this.isContentControl(target) || this.hasSelectedText()) {
        return
      }

      if (this.isAtTop(target)) {
        this.pendingTouch = event
      }
    } else {
      if (
        target instanceof Element &&
        target.closest('button, a, input, select, textarea, [role="button"], [contenteditable]')
      ) {
        return
      }

      this.controller.start(event, this.panel)
      this.dragging = true
      this.prepareFollowers()
    }
  }

  /**
   * Moves an active drag unless the sheet was covered. @param event Touch position.
   * @param {TouchEvent} event
   */
  onMove(event: TouchEvent): void {
    if (!this.panel) {
      return
    }

    if (this.sheetDragDisabled() || event.touches.length !== 1) {
      this.onCancel()

      return
    }

    if (this.pendingTouch) {
      // Leave gestures already handled by another control and native scrolling alone.
      if (event.defaultPrevented || !event.cancelable || this.hasSelectedText() || !this.isAtTop(event.target)) {
        this.onCancel()

        return
      }

      const origin = this.pendingTouch.touches[0]
      const touch = event.touches[0]
      const distance = touch.clientY - origin.clientY
      const horizontalDistance = Math.abs(touch.clientX - origin.clientX)

      if (Math.max(Math.abs(distance), horizontalDistance) < CONTENT_DRAG_SLOP) {
        return
      }

      if (distance <= 0 || horizontalDistance >= distance) {
        this.onCancel()

        return
      }

      this.controller.start(this.pendingTouch, this.panel)
      this.dragging = true
      this.pendingTouch = undefined
      this.prepareFollowers()
    }

    this.controller.move(event, this.panel)
    this.closeKeyboard()
    this.followPull()
  }

  /** Dismisses after a completed drag. */
  onEnd(): void {
    this.pendingTouch = undefined

    if (!this.panel) {
      return
    }

    if (this.sheetDragDisabled()) {
      this.onCancel()

      return
    }

    if (this.controller.end(this.panel, this.sheetDragFromContent() ? 'distance' : 'distance-or-flick')) {
      this.haptics.swipeThreshold()
      this.closedField = undefined
      this.dismissed.emit(this.controller.remaining)
    } else {
      this.releaseFollowers()
      this.reopenKeyboard()
    }

    this.dragging = false
  }

  /** Resets a cancelled drag without closing the sheet. */
  onCancel(): void {
    this.pendingTouch = undefined

    if (!this.panel) {
      return
    }

    this.controller.cancel(this.panel)

    if (this.dragging) {
      this.releaseFollowers()
      this.reopenKeyboard()
      this.dragging = false
    }
  }

  /** Cancels the previous gesture and uses the panel bound by Angular. */
  private syncPanel(): void {
    const panel = this.awSheetDrag()

    untracked(() => {
      this.onCancel()
      this.panel = panel
    })
  }

  /**
   * Closes the keyboard once the pull moves the sheet. The page grows when the keyboard leaves, and the sheet
   * cannot draw behind the keyboard, so waiting for release leaves the sheet stuck at the keyboard edge.
   */
  private closeKeyboard(): void {
    const active = this.panel?.ownerDocument.activeElement

    // Only text fields: every sheet focuses its own panel when it opens.
    if (this.controller.moved && isKeyboardField(active) && this.panel?.contains(active)) {
      this.closedField = active
      active.blur()
    }
  }

  /** Returns focus to the field the pull closed when the sheet springs back, so typing can continue. */
  private reopenKeyboard(): void {
    this.closedField?.focus({ preventScroll: true })
    this.closedField = undefined
  }

  /** Stops the retained pages and backdrop animating and gives them their own layers for the pull. */
  private prepareFollowers(): void {
    for (const panel of this.sheetDragPanels()) {
      panel.style.animation = 'none'
      panel.style.transition = 'none'
      panel.style.willChange = 'transform'
    }

    const backdrop = this.sheetDragBackdrop()

    if (backdrop) {
      backdrop.style.animation = 'none'
      backdrop.style.transition = 'none'
      backdrop.style.willChange = 'opacity'
    }
  }

  /** Moves the retained pages and fades the backdrop with the finger. Only compositor properties change. */
  private followPull(): void {
    const transform = this.panel.style.transform

    for (const panel of this.sheetDragPanels()) {
      panel.style.transform = transform
    }

    const backdrop = this.sheetDragBackdrop()

    if (backdrop) {
      backdrop.style.opacity = `${1 - this.controller.progress}`
    }
  }

  /** Springs the retained pages and backdrop back with the panel and drops their layers. */
  private releaseFollowers(): void {
    const active = this.panel
    // The panel has no transition when motion is reduced.
    const reducedMotion = active.style.transition === 'none'

    for (const panel of this.sheetDragPanels()) {
      panel.style.transition = active.style.transition
      panel.style.transform = active.style.transform
      panel.style.willChange = ''
    }

    const backdrop = this.sheetDragBackdrop()

    if (backdrop) {
      backdrop.style.transition = reducedMotion ? 'none' : `opacity ${RESTORE_TIMING}`
      backdrop.style.opacity = ''
      backdrop.style.willChange = ''
    }
  }

  /**
   * Keeps active editing and controls with their own gestures out of content pulls.
   * @param {EventTarget | null} target Touch target.
   * @returns {boolean} Whether the control owns this touch.
   */
  private isContentControl(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false
    }

    if (target.closest('select, [role="slider"], .cdk-drag-handle')) {
      return true
    }

    const input = target.closest('input')

    if (input instanceof HTMLInputElement && !TEXT_INPUT_TYPES.has(input.type)) {
      return true
    }

    const editor = target.closest('input, textarea, [contenteditable]:not([contenteditable="false"])')

    return !!editor && editor.contains(this.host.nativeElement.ownerDocument.activeElement)
  }

  /**
   * Leaves selected text in this body to the browser, including selection from a long press.
   * @returns {boolean} Whether the sheet content has a text selection.
   */
  private hasSelectedText(): boolean {
    const body = this.host.nativeElement
    const selection = body.ownerDocument.getSelection()

    return (
      !!selection &&
      !selection.isCollapsed &&
      (body.contains(selection.anchorNode) || body.contains(selection.focusNode))
    )
  }

  /**
   * Checks every scroll area between the touched row and the sheet body. @param target Touch target. @returns Whether content can be pulled down.
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  private isAtTop(target: EventTarget | null): boolean {
    let element: Element | null = target instanceof Element ? target : this.host.nativeElement

    while (element) {
      const overflow = getComputedStyle(element).overflowY

      if ((overflow === 'auto' || overflow === 'scroll') && element.scrollTop > 1) {
        return false
      }

      if (element === this.host.nativeElement) {
        break
      }

      element = element.parentElement
    }

    return true
  }
}
